import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';

const AdminDelivery = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [editingDelivery, setEditingDelivery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [filteredDeliveryPersons, setFilteredDeliveryPersons] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    vehicle_type: '',
    license_number: '',
    is_active: true,
    is_verified: true
  });

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
  }, []);

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (user) => {
    // Le statut peut être basé sur is_active ou un autre champ
    const isActive = user.is_active !== undefined ? user.is_active : true;
    return isActive ? (
      <span className="badge badge-success">Disponible</span>
    ) : (
      <span className="badge badge-danger">Hors ligne</span>
    );
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = deliveryPersons;

    if (filters.search) {
      filtered = filtered.filter(delivery => {
        const name = delivery.name || '';
        const email = delivery.email || '';
        const phone = delivery.phone || '';
        return (
          name.toLowerCase().includes(filters.search.toLowerCase()) ||
          email.toLowerCase().includes(filters.search.toLowerCase()) ||
          phone.toLowerCase().includes(filters.search.toLowerCase())
        );
      });
    }

    if (filters.status) {
      filtered = filtered.filter(delivery =>
        filters.status === 'active' ? delivery.is_active : !delivery.is_active
      );
    }

    // Appliquer le tri
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'name':
            aValue = (a.name || '').toLowerCase();
            bValue = (b.name || '').toLowerCase();
            break;
          case 'email':
            aValue = (a.email || '').toLowerCase();
            bValue = (b.email || '').toLowerCase();
            break;
          case 'status':
            aValue = a.is_active ? 1 : 0;
            bValue = b.is_active ? 1 : 0;
            break;
          case 'created_at':
            aValue = new Date(a.created_at || 0).getTime();
            bValue = new Date(b.created_at || 0).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredDeliveryPersons(filtered);
    setCurrentPage(1);
  }, [deliveryPersons, filters, sortField, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const deliveryData = await adminApi.getDeliveryPersons();
      let deliveryList = [];
      if (Array.isArray(deliveryData)) {
        deliveryList = deliveryData;
      } else if (deliveryData?.data) {
        // Si c'est une pagination Laravel
        deliveryList = Array.isArray(deliveryData.data) ? deliveryData.data : deliveryData.data.data || [];
      } else if (deliveryData?.success && deliveryData?.data) {
        deliveryList = Array.isArray(deliveryData.data) ? deliveryData.data : [];
      }
      console.log('Livreurs chargés:', deliveryList.length, deliveryList);
      setDeliveryPersons(deliveryList);
    } catch (error) {
      console.error('Erreur lors du chargement des livreurs:', error);
      addNotification('Erreur lors du chargement des livreurs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: ''
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      phone: '',
      vehicle_type: '',
      license_number: '',
      is_active: true,
      is_verified: true
    });
    setEditingDelivery(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const deliveryData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        vehicle_type: formData.vehicle_type || null,
        license_number: formData.license_number || null,
        is_active: formData.is_active === true || formData.is_active === 'true',
        is_verified: formData.is_verified === true || formData.is_verified === 'true',
      };

      // Ajouter le mot de passe seulement si c'est une création ou s'il est modifié
      if (!editingDelivery || formData.password) {
        if (!formData.password || formData.password.length < 8) {
          addNotification('Le mot de passe doit contenir au moins 8 caractères', 'error');
          return;
        }
        deliveryData.password = formData.password;
      }

      let savedDelivery;
      if (editingDelivery) {
        const response = await adminApi.updateDeliveryPerson(editingDelivery.id, deliveryData);
        savedDelivery = response?.data || response;

        if (!savedDelivery || !savedDelivery.id) {
          throw new Error('Réponse invalide du serveur');
        }

        // Ne pas mettre à jour manuellement, recharger depuis le serveur
        // setDeliveryPersons(prev => prev.map(delivery =>
        //   delivery.id === editingDelivery.id ? savedDelivery : delivery
        // ));
        addNotification('Livreur modifié avec succès', 'success');
      } else {
        const response = await adminApi.createDeliveryPerson(deliveryData);
        savedDelivery = response?.data || response;

        if (!savedDelivery || !savedDelivery.id) {
          throw new Error('Réponse invalide du serveur');
        }

        // Ne pas ajouter manuellement, recharger depuis le serveur
        // setDeliveryPersons(prev => [...prev, savedDelivery]);
        addNotification('Livreur créé avec succès', 'success');
      }

      setShowAddModal(false);
      resetForm();
      // Attendre un peu pour que le backend termine la transaction
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la sauvegarde';
      addNotification(errorMessage, 'error');
    }
  };

  const handleEdit = (delivery) => {
    setEditingDelivery(delivery);
    setFormData({
      name: delivery.name || '',
      email: delivery.email || '',
      password: '', // Ne pas pré-remplir le mot de passe
      phone: delivery.phone || '',
      vehicle_type: delivery.vehicle_type || delivery.vehicle || '',
      license_number: delivery.license_number || delivery.licensePlate || '',
      is_active: delivery.is_active !== undefined ? delivery.is_active : true,
      is_verified: delivery.is_verified !== undefined ? delivery.is_verified : true
    });
    setShowAddModal(true);
  };

  const handleDeleteClick = (delivery) => {
    setSelectedDelivery(delivery);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedDelivery) return;

    try {
      await adminApi.deleteDeliveryPerson(selectedDelivery.id);
      // Ne pas supprimer manuellement, recharger depuis le serveur
      // setDeliveryPersons(prev => prev.filter(delivery => delivery.id !== selectedDelivery.id));
      setShowDeleteModal(false);
      setSelectedDelivery(null);
      addNotification('Livreur supprimé avec succès', 'success');
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la suppression:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la suppression';
      addNotification(errorMessage, 'error');
    }
  };

  const toggleStatus = async (delivery) => {
    try {
      const currentStatus = delivery.is_active !== undefined ? delivery.is_active : true;
      await adminApi.updateDeliveryPerson(delivery.id, {
        ...delivery,
        is_active: !currentStatus
      });
      // const savedDelivery = updatedDelivery.data || updatedDelivery;
      // Ne pas mettre à jour manuellement, recharger depuis le serveur
      // setDeliveryPersons(prev => prev.map(d => d.id === delivery.id ? savedDelivery : d));
      addNotification(`Livreur ${!currentStatus ? 'activé' : 'désactivé'} avec succès`, 'success');
      setTimeout(() => {
        loadData();
      }, 500);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      addNotification('Erreur lors de la mise à jour du statut', 'error');
    }
  };

  // Fonction pour exporter les livreurs en CSV
  const handleExport = () => {
    try {
      const deliveriesToExport = filteredDeliveryPersons.length > 0 ? filteredDeliveryPersons : deliveryPersons;

      if (deliveriesToExport.length === 0) {
        addNotification('Aucun livreur à exporter', 'warning');
        return;
      }

      const headers = [
        'ID',
        'Nom',
        'Email',
        'Téléphone',
        'Type de Véhicule',
        'Numéro de Licence',
        'Statut',
        'Vérifié',
        'Date de création'
      ];

      const csvRows = deliveriesToExport.map(delivery => {
        const row = [
          delivery.id || '',
          `"${(delivery.name || '').replace(/"/g, '""')}"`,
          `"${(delivery.email || '').replace(/"/g, '""')}"`,
          delivery.phone || '',
          delivery.vehicle_type || delivery.vehicle || '',
          delivery.license_number || delivery.licensePlate || '',
          delivery.is_active ? 'Actif' : 'Inactif',
          delivery.is_verified ? 'Oui' : 'Non',
          delivery.created_at ? new Date(delivery.created_at).toLocaleDateString('fr-FR') : ''
        ];
        return row.join(',');
      });

      const csvContent = [
        headers.join(','),
        ...csvRows
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const date = new Date().toISOString().split('T')[0];
      const filename = `livreurs_${date}.csv`;
      link.setAttribute('download', filename);

      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification(`${deliveriesToExport.length} livreur(s) exporté(s) avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      addNotification('Erreur lors de l\'export des livreurs', 'error');
    }
  };

  // Fonction pour formater les dates (non utilisée pour le moment)
  // const formatDate = (dateString) => {
  //   if (!dateString) return 'N/A';
  //   const date = new Date(dateString);
  //   return date.toLocaleDateString('fr-FR', {
  //     year: 'numeric',
  //     month: 'long',
  //     day: 'numeric'
  //   });
  // };

  // Pagination
  const totalPages = Math.ceil(filteredDeliveryPersons.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentDeliveries = filteredDeliveryPersons.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Chargement des livreurs...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />

      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Outils</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Livreurs</span>
      </div>

      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="page-title">Gestion des Livreurs</h1>
            <p className="page-subtitle">
              Créer et gérer les livreurs pour la livraison des commandes
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowAddModal(true);
            }}
            className="btn btn-primary"
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
            </svg>
            Créer un livreur
          </button>
        </div>
      </div>

      {/* Filtres et Actions */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap gap-3 flex-1">
          {/* Recherche */}
          <input
            type="text"
            name="search"
            placeholder="Rechercher un livreur..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />

          {/* Filtre par statut */}
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="">Tous les statuts</option>
            <option value="active">Actifs</option>
            <option value="inactive">Inactifs</option>
          </select>

          {/* Bouton pour effacer les filtres */}
          <button
            onClick={clearFilters}
            className="btn btn-secondary text-sm"
          >
            Effacer
          </button>
        </div>
      </div>

      {/* Delivery Persons DataTable */}
      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Liste des Livreurs
            <span className="text-sm text-gray-500 ml-2">
              ({filteredDeliveryPersons.length} livreur{filteredDeliveryPersons.length > 1 ? 's' : ''})
            </span>
          </h3>
          <div className="table-actions">
            <button
              onClick={handleExport}
              className="btn btn-secondary"
              disabled={filteredDeliveryPersons.length === 0}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button
                    onClick={() => handleSort('name')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Livreur
                    {sortField === 'name' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Contact</th>
                <th>Véhicule</th>
                <th>Statut</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentDeliveries.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <p className="text-lg font-medium">Aucun livreur trouvé</p>
                      <p className="text-sm">Les livreurs apparaîtront ici</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentDeliveries.map((delivery) => {
                  const name = delivery.name || 'N/A';
                  const email = delivery.email || 'N/A';
                  const phone = delivery.phone || 'N/A';
                  const vehicle = delivery.vehicle_type || delivery.vehicle || 'N/A';
                  const licensePlate = delivery.license_number || delivery.licensePlate || 'N/A';
                  const isActive = delivery.is_active !== undefined ? delivery.is_active : true;

                  return (
                    <tr key={delivery.id}>
                      <td>
                        <div className="flex items-center">
                          <div
                            className="w-10 h-10 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg flex items-center justify-center text-white font-semibold mr-3"
                            style={{ minWidth: '40px', minHeight: '40px' }}
                          >
                            {name && name.length > 0 ? name.charAt(0).toUpperCase() : '?'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900 dark:text-white">
                              {name}
                            </div>
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div>{phone}</div>
                      </td>
                      <td>
                        <div className="font-medium">{vehicle}</div>
                        {licensePlate !== 'N/A' && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {licensePlate}
                          </div>
                        )}
                      </td>
                      <td>
                        {getStatusBadge(delivery)}
                      </td>
                      <td>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Bouton Modifier - Orange/Warning */}
                          <button
                            onClick={() => handleEdit(delivery)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#f59e0b' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#d97706'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#f59e0b'}
                            title="Modifier"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Modifier
                          </button>

                          {/* Bouton Activer/Désactiver - Vert ou Gris */}
                          <button
                            onClick={() => toggleStatus(delivery)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: isActive ? '#10b981' : '#6b7280' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = isActive ? '#059669' : '#4b5563'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = isActive ? '#10b981' : '#6b7280'}
                            title={isActive ? 'Désactiver' : 'Activer'}
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              {isActive ? (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                              ) : (
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                              )}
                            </svg>
                            {isActive ? 'Désactiver' : 'Activer'}
                          </button>

                          {/* Bouton Supprimer - Rouge/Danger */}
                          <button
                            onClick={() => handleDeleteClick(delivery)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                            style={{ backgroundColor: '#ef4444' }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                            title="Supprimer"
                          >
                            <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            Supprimer
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-4 py-3">
            <div className="text-sm text-gray-700">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredDeliveryPersons.length)} sur {filteredDeliveryPersons.length} livreur{filteredDeliveryPersons.length > 1 ? 's' : ''}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <span className="flex items-center px-4 text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal d'ajout/modification */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => { setShowAddModal(false); resetForm(); }}>
          <div className="modal-content max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">
                {editingDelivery ? 'Modifier un livreur' : 'Créer un livreur'}
              </h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => { setShowAddModal(false); resetForm(); }}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-body">
              <div className="modal-form-group">
                <label className="modal-form-label">Nom complet *</label>
                <input
                  type="text"
                  className="modal-form-input"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Ex: Ibrahim Coulibaly"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Email *</label>
                <input
                  type="email"
                  className="modal-form-input"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  placeholder="Ex: ibrahim@delivery.ml"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">
                  Mot de passe {editingDelivery ? '(laisser vide pour ne pas modifier)' : '*'}
                </label>
                <input
                  type="password"
                  className="modal-form-input"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingDelivery}
                  minLength={8}
                  placeholder="Minimum 8 caractères"
                />
                {editingDelivery && (
                  <p className="text-sm text-gray-500 mt-1">
                    Laissez vide si vous ne souhaitez pas modifier le mot de passe
                  </p>
                )}
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Téléphone</label>
                <input
                  type="tel"
                  className="modal-form-input"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="Ex: +223 70 11 22 33"
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Type de Véhicule</label>
                <select
                  className="modal-form-select"
                  value={formData.vehicle_type}
                  onChange={(e) => setFormData({ ...formData, vehicle_type: e.target.value })}
                >
                  <option value="">Sélectionner</option>
                  <option value="Vélo">Vélo</option>
                  <option value="Moto">Moto</option>
                  <option value="Voiture">Voiture</option>
                  <option value="Camionnette">Camionnette</option>
                </select>
              </div>

              <div className="modal-form-group">
                <label className="modal-form-label">Plaque d'Immatriculation</label>
                <input
                  type="text"
                  className="modal-form-input"
                  value={formData.license_number}
                  onChange={(e) => setFormData({ ...formData, license_number: e.target.value })}
                  placeholder="ML-1234-AB ou N/A"
                />
              </div>

              <div className="modal-form-group">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="modal-form-label">Compte actif</span>
                </label>
              </div>

              <div className="modal-form-group">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_verified}
                    onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                    className="mr-2"
                  />
                  <span className="modal-form-label">Compte vérifié</span>
                </label>
              </div>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => { setShowAddModal(false); resetForm(); }}
                  className="btn btn-secondary"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                >
                  {editingDelivery ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteModal && selectedDelivery && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Confirmer la suppression</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDeleteModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer le livreur <strong>{selectedDelivery.name}</strong> ?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Cette action est irréversible. Toutes les données associées à ce livreur seront également supprimées.
              </p>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDelivery;
